import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { ChatEngine } from '@/state/chatStore';
import { resolveConfig } from '@/core/config';
const ChatContext = createContext(null);
export function ChatProvider({ config, engine: external, children }) {
    const value = useMemo(() => {
        const engine = external ?? new ChatEngine(resolveConfig(config));
        const resolved = engine.getConfig();
        // Features are the intersection of what's configured and what the active
        // transport can actually do, so pointing at a backend without (say) a
        // feedback endpoint hides the thumbs instead of firing calls that 404.
        const { capabilities } = resolved.transport;
        return {
            engine,
            config: {
                ...resolved,
                features: {
                    ...resolved.features,
                    feedback: resolved.features.feedback && capabilities.feedback,
                    handoff: resolved.features.handoff && capabilities.handoff,
                },
            },
        };
        // A new engine per config identity is intentional; callers pass a stable object.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [external, config]);
    useEffect(() => {
        const { engine, config: resolved } = value;
        if (resolved.presentation === 'fullscreen' || resolved.openOnLoad)
            void engine.init();
        return () => {
            if (!external)
                engine.dispose();
        };
    }, [value, external]);
    return _jsx(ChatContext.Provider, { value: value, children: children });
}
export function useChatContext() {
    const ctx = useContext(ChatContext);
    if (!ctx)
        throw new Error('useChatContext must be used inside <ChatProvider>');
    return ctx;
}
/**
 * Subscribe to a slice of chat state; re-renders only when that slice changes.
 *
 * Wrapped in `useShallow` deliberately. zustand v5 compares snapshots with
 * `Object.is`, so a selector returning a new object or array each call would
 * spin React forever. Shallow comparison makes that whole class of mistake
 * impossible for any selector written later, including inline ones.
 */
export function useChatState(selector) {
    const { engine } = useChatContext();
    return useStore(engine.store, useShallow(selector));
}
export function useChatActions() {
    return useChatContext().engine.actions;
}
export function useChatConfig() {
    return useChatContext().config;
}
